import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import GoogleMapReact from 'google-map-react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';

import CircularProgress from '@material-ui/core/CircularProgress';
import {
  Grid,
  Typography,
  Paper,
  Collapse,
  IconButton,
  Link,
  Button,
  Modal,
  useMediaQuery,
  TextField,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import PinDropIcon from '@mui/icons-material/PinDrop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import {
  mapOptions,
  BACKGROUND_COLOR,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY_LIGHT,
  COLOR_ACCENT_1,
  COLOR_PRIMARY,
  COLOR_RED,
} from 'src/const';
import { EditTailoredRegionNameModal, ErrorDialog } from '.';
import { TailoredRegionPointer } from '../HOC/Pointer';
import { OnboardingModalPropTypes, SearchProp } from 'src/interfaces/iproptypes';
import { callApi } from 'src/core/callApi';
import { TailoredRegionMarkers } from 'src/interfaces/idashboard';
import postOnSlack from 'src/core/postOnSlack';
import { useSelector } from 'react-redux';
import { User } from 'src/interfaces/iuser';

const RIPPLE_SIZE = 100;
const RIPPLE_COLOR = COLOR_ACCENT_1;

const useStyles = makeStyles((theme: Theme) => ({
  customModalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    margin: 0,
    padding: 0,
  },
  paperModal: {
    backgroundColor: BACKGROUND_COLOR,
    width: 600,
    padding: theme.spacing(2),
    animation: 'fade-scale-in 0.3s ease forwards',
  },
  tailoredRegionMapContainer: {
    height: 400,
  },
  tailoredRegionMapAlert: {
    height: 'max(50px, 10vh)',
    width: '100%',
    overflowY: 'auto',
    margin: 'auto',
    marginBottom: 15,
    boxSizing: 'border-box',
  },
  tailoredRegionMapTopButtonContainer: {
    marginTop: -20,
    width: 'auto',
    flexWrap: 'nowrap',
  },
  button: {
    background: COLOR_PRIMARY_DARK,
    borderRadius: 5,
    border: 0,
    color: 'white',
    padding: '5px 30px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
    },
    fontSize: 16,
  },
  ripple: {
    position: 'absolute',
    top: 0,
    left: 0,
    marginTop: -RIPPLE_SIZE / 2,
    marginLeft: -RIPPLE_SIZE / 2,
    width: RIPPLE_SIZE,
    height: RIPPLE_SIZE,
    animation: `$rippleAnimation 1000ms ${theme.transitions.easing.easeOut}`,
    animationIterationCount: 'infinite',
    opacity: 0,
    borderRadius: '50%',
    boxShadow: `0 0 6px 3px ${RIPPLE_COLOR}`,
  },
  '@keyframes rippleAnimation': {
    '0%': {
      transform: 'scale(0.1, 0.1)',
      opacity: 0,
    },
    '1%': {
      opacity: 1,
    },
    '50% ': {
      opacity: 1,
    },
    '100%': {
      transform: 'scale(1.2, 1.2)',
      opacity: 0,
    },
  },
}));

const initiateMapFromDrawing = (polygon, drawingManager, setIsLoading) => {
  polygon && polygon.setMap(null);
  showDrawingTool(drawingManager);
  setIsLoading(false);
};

const showDrawingTool = drawingManager => {
  drawingManager.setOptions({
    drawingControl: true,
  });
};

const removeDrawingTool = drawingManager => {
  drawingManager.setDrawingMode(null);
  drawingManager.setOptions({
    drawingControl: false,
  });
};

const getTailorRegionAllowed = (tailoredListings: number, user: User) => {
  if (tailoredListings < 10) {
    return false;
  }
  if (tailoredListings > 30000 && !user.email.endsWith("@airbtics.com")) {
    return false;
  }  
  return true;
};

const updateTailoredRegionName = async (
  oldTailorRegionName: string,
  newTailorRegionName: string
): Promise<{ success: boolean }> => {
  const editTailorRegionNameAPI =
    'https://oz287au41h.execute-api.us-east-2.amazonaws.com/default/createTailorRegion';

  const editTailorRegionConfig = {
    queryParams: {
      generatedName: oldTailorRegionName,
      customName: newTailorRegionName,
    },
  };

  try {
    const editTailorRegionResponse = await callApi(
      editTailorRegionNameAPI,
      editTailorRegionConfig,
      true
    );
    const editTailorRegionResponseObj = editTailorRegionResponse.message;
    const isCustomNameAllowed: boolean = editTailorRegionResponseObj.isCustomNameAllowed;
    if (isCustomNameAllowed) {
      return { success: true };
    }
  } catch (e) {
    console.log('Error while calling /createTailorRegion is', e);
  }
  return { success: false };
};

const handleTailorRegionCreation = (
  polygon,
  map,
  maps,
  drawingManager,
  setTailorRegionName,
  polygonArray,
  setTailoredListings,
  setIsLoading,
  setApiCallStatus,
  initiateMapFromDrawing: (polygon: any, drawingManager: any, setIsLoading) => void,
  setDisableButton,
  tailorRegionName,
  user
) => {
  removeDrawingTool(drawingManager);

  let polygonString = '';
  polygonArray.forEach((polygon, i) => {
    polygonString += `(${polygon.toString().replace(',', ' ')}),`;
  });
  polygonString = polygonString.slice(0, -1);

  let listingUrl = `https://8jmjhnmtei.execute-api.us-east-2.amazonaws.com/default/getTotalSupplyForRegion?polygon=${polygonString}`;

  axios.get(listingUrl).then(res => {
    setTailoredListings(res.data.message);
    let isTailorRegionAllowed = getTailorRegionAllowed(res.data.message, user);
    if (!isTailorRegionAllowed) {
      setApiCallStatus('notAllowed');
      initiateMapFromDrawing(polygon, drawingManager, setIsLoading);
      return;
    }

    const createTailorRegionUrl =
      'https://oz287au41h.execute-api.us-east-2.amazonaws.com/default/createTailorRegion';

    const createTailorRegionConfig = {
      queryParams: {
        countryCode: res.data.countryCode,
        polygon: polygonString,
      },
    };

    setTimeout(async () => {
      const params = new URLSearchParams(window.location.search);
      const hasName = params.get('lat') && params.get('lng') && params.get('name');
      try {
        try {
          let createTailorRegionResponse = await callApi(
            createTailorRegionUrl,
            createTailorRegionConfig,
            true
          );
          let createTailoreRegionResponseObj = createTailorRegionResponse.message;
          let regionNameFromResponse = createTailoreRegionResponseObj['district'];
          if (!hasName) {
            setTailorRegionName(regionNameFromResponse);
          } else {
            const isTailoredRegionNameUpdated = await updateTailoredRegionName(
              regionNameFromResponse,
              tailorRegionName
            );
            console.log('isNameChangesuccess', isTailoredRegionNameUpdated.success);
            if (!isTailoredRegionNameUpdated.success) {
              console.log('Executed', params.get('name'));
              setTailorRegionName(regionNameFromResponse);
              window.alert(
                `Region Name "${params.get(
                  'name'
                )}" already exists so we have auto-assigned a name to this market. Feel free to customize it by clicking on the text box containing the market name.`
              );
            }
          }
          setApiCallStatus('success');
          setIsLoading(false);
          setDisableButton(false);
        } catch (e) {
          console.log('Error while calling /createTailorRegion is', e);
          setApiCallStatus('failed');
          initiateMapFromDrawing(polygon, drawingManager, setIsLoading);
        }
      } catch (e) {
        console.log('Unhandled error point 50 ', e);
        setApiCallStatus('failed');
        initiateMapFromDrawing(polygon, drawingManager, setIsLoading);
      }
    }, 1500);
  });
};

const OnboardingModal = ({
  getTokenThunk,
  setIsTailoredRegionOpen,
  tailoredRegionMarkers,
  getTailoredRegionMarkers,
}: OnboardingModalPropTypes) => {
  const classes = useStyles();

  const handleMapChange = useCallback(
    ({ bounds }) => {
      getTailoredRegionMarkers(bounds);
    },
    [getTailoredRegionMarkers]
  );

  const user = useSelector(state => (state as any).dashboard.user);

  const [tailorRegionStartInfo, setTailorRegionStartInfo] = useState(true);
  const [open, setOpen] = useState(true);
  const isLoggedIn = Boolean(getTokenThunk());
  const history = useHistory();
  const [triggerRectangle, setTriggerRectangle] = useState(false);
  const [tailoredListings, setTailoredListings] = useState(0);
  const [tailorRegionName, setTailorRegionName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiCallStatus, setApiCallStatus] = useState('');
  const [disableButton, setDisableButton] = useState(true);
  const [centerLat, setCenterLat] = useState(39.210878338011376);
  const [centerLng, setCenterLng] = useState(-101.51684523289367);
  const [homeLat, setHomeLat] = useState(39.210878338011376);
  const [homeLng, setHomeLng] = useState(-101.51684523289367);
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [drawingManagerCopy, setDrawingManagerCopy] = useState(null as any);
  const [polygonCopy, setPolygonCopy] = useState(null as any);
  const [editTailoredRegionName, setEditTailoredRegionName] = useState<boolean>(false);
  const [polygonAssistant, setPolygonAssistant] = useState<{
    isOpen: boolean;
    top: number;
    left: number;
  }>({ isOpen: false, top: 0, left: 0 });

  const [wrongLatLng, setWrongLatLng] = useState<boolean>(false);

  const scrollModal = useMediaQuery('(max-height:550px)');

  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    const lat = params.get('lat');
    const lng = params.get('lng');
    const name = params.get('name');

    if (lat && lng) {
      if (isNaN(Number(lat)) || isNaN(Number(lng))) {
        setWrongLatLng(true);
      } else {
        setCenterLat(Number(lat));
        setCenterLng(Number(lng));
        setHomeLat(Number(lat));
        setHomeLng(Number(lng));
        setLocationAllowed(true);

        if (name) {
          const name_from_url =
            params
              .get('name')
              ?.toString()
              .trim() !== ''
              ? (params.get('name') + '').trim()
              : '';
          console.log('Name from url: ', name_from_url);

          if (name_from_url !== '') {
            setTailorRegionName(name_from_url);
          }
        }
      }
    }
    // console.log(`%cLat & Lng: (${lat} , ${lng})`, 'color: cyan; font-size: 30px');
  }, []);

  const loadPolygonDrawButtonAssistant = next => {
    setTimeout(() => {
      const polygonDrawButton: any = document.querySelector("button[aria-label='Draw a shape']");
      if (polygonDrawButton) {
        const rect = polygonDrawButton.getBoundingClientRect();
        // console.log(`%cTop: ${rect.top}, left: ${rect.left}`, 'color: cyan; font-size: 30px');
        setPolygonAssistant(prev => ({ top: rect.top, left: rect.left, isOpen: true }));
        next();

        const removeAssistant = () => {
          setPolygonAssistant(prev => ({ ...prev, isOpen: false }));
          polygonDrawButton.removeEventListener('click', removeAssistant);
        };

        polygonDrawButton.addEventListener('click', removeAssistant);
      }
    }, 1000);
  };

  const callbackAfterMapIsLoaded = (map, maps) => {
    const tilesLoadListener = maps.event.addListener(map, 'tilesloaded', () =>
      loadPolygonDrawButtonAssistant(() => maps.event.removeListener(tilesLoadListener))
    );
  };

  const locationPin = useMemo(() => {
    return (
      <div>
        <div className={classes.ripple}></div>
        <PinDropIcon
          style={{
            fontSize: 40,
            position: 'absolute',
            top: -20,
            left: -20,
          }}
        />
      </div>
    );
  }, [centerLat, centerLng]);

  let markers = useMemo(() => {
    if (tailoredRegionMarkers && tailoredRegionMarkers.length > 0) {
      return tailoredRegionMarkers.map((item: TailoredRegionMarkers, index: number) => (
        <TailoredRegionPointer key={index} lat={item.latitude} lng={item.longitude} data={item} />
      ));
    }
    return;
  }, [tailoredRegionMarkers]);

  const tailoredRegionApiIsLoaded = (map, maps) => {
    const drawingManager = new maps.drawing.DrawingManager({
      drawingControl: true,
      drawingControlOptions: {
        position: maps.ControlPosition.TOP_LEFT,
        drawingModes: [maps.drawing.OverlayType.POLYGON],
      },
      polygonOptions: {
        editable: true,
        draggable: false,
      },
    });
    setDrawingManagerCopy(drawingManager);
    drawingManager.setMap(map);

    maps.event.addListener(drawingManager, 'polygoncomplete', polygon => {
      setPolygonCopy(polygon);
      let polygonArray: number[][] = [];
      let polygonPaths = polygon.getPath().getArray();
      polygonPaths.forEach(path => {
        polygonArray.push([path.lat(), path.lng()]);
      });
      // console.log('polygonArray', polygonArray);
      setIsLoading(true);
      setTriggerRectangle(true);
      handleTailorRegionCreation(
        polygon,
        map,
        maps,
        drawingManager,
        setTailorRegionName,
        polygonArray,
        setTailoredListings,
        setIsLoading,
        setApiCallStatus,
        initiateMapFromDrawing,
        setDisableButton,
        tailorRegionName,
        user
      );

      maps.event.addListener(polygon.getPath(), 'set_at', () => {
        let polygonArray: number[][] = [];
        let polygonPaths = polygon.getPath().getArray();
        polygonPaths.forEach(path => {
          polygonArray.push([path.lat(), path.lng()]);
        });
        setIsLoading(true);
        setTriggerRectangle(true);
        handleTailorRegionCreation(
          polygon,
          map,
          maps,
          drawingManager,
          setTailorRegionName,
          polygonArray,
          setTailoredListings,
          setIsLoading,
          setApiCallStatus,
          initiateMapFromDrawing,
          setDisableButton,
          tailorRegionName,
          user
        );
      });

      maps.event.addListener(polygon.getPath(), 'insert_at', () => {
        let polygonArray: number[][] = [];
        let polygonPaths = polygon.getPath().getArray();
        polygonPaths.forEach(path => {
          polygonArray.push([path.lat(), path.lng()]);
        });
        setIsLoading(true);
        setTriggerRectangle(true);
        handleTailorRegionCreation(
          polygon,
          map,
          maps,
          drawingManager,
          setTailorRegionName,
          polygonArray,
          setTailoredListings,
          setIsLoading,
          setApiCallStatus,
          initiateMapFromDrawing,
          setDisableButton,
          tailorRegionName,
          user
        );
      });
    });
  };

  const handleTailoredRegionMapChange = useCallback(({ center }) => {
    const centerLat = center.lat();
    const centerLng = center.lng();
    setCenterLat(centerLat);
    setCenterLng(centerLng);
  }, []);

  useEffect(() => {
    if (!params.has('lat') || !params.has('lng') || wrongLatLng) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setHomeLat(position.coords.latitude);
          setHomeLng(position.coords.longitude);
          setCenterLat(position.coords.latitude);
          setCenterLng(position.coords.longitude);
          setLocationAllowed(true);
        },
        error => {
          console.error('Error Code = ' + error.code + ' - ' + error.message);
        }
      );
    }
  }, []);

  const setCustomMarket = async () => {
    // setIsTailoredRegionOpen(false);

    if (tailoredListings >= 10000) {
      const userEmail = localStorage.getItem('user-email');
      postOnSlack(
        'customer',
        `[MARKET HAS MORE THAN 10K LISTINGS]: user: ${userEmail} Market Name: ${tailorRegionName}`
      );
    }

    if (!isLoggedIn){
      window.localStorage.setItem('tailoredRegion', tailorRegionName)
    }

    history.push(`/airbnb-data/0/0/${tailorRegionName}`);

    // Updating the local storage when user create any new tailor region
    const search_attempt_history = localStorage.getItem('investment-search-attempts');
    const noOfUserSearch = search_attempt_history ? parseInt(search_attempt_history) : 0;
    localStorage.setItem('investment-search-attempts', (noOfUserSearch + 1).toString());
    window.location.reload();
  };

  const resetTailoredRegion = () => {
    initiateMapFromDrawing(polygonCopy, drawingManagerCopy, setIsLoading);
  };

  const closeTailoredRegion = () => {
    setIsTailoredRegionOpen(false);
    window.history.pushState({}, '', window.location.pathname);
    if (window.location.pathname.search('tailored/t') === 1) {
      history.push(`/`);
    }
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Tailored Region - Airbtics</title>
      </Helmet>

      <Modal
        open={open}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Grid
          container
          direction="column"
          alignItems="center"
          justify="center"
          style={{ minHeight: '100vh' }}
        >
          {/* Custom backdrop */}
          <div className={classes.customModalBackdrop} onClick={closeTailoredRegion} />
          <div
            className={`${classes.paperModal} modal-custom`}
            style={{ overflowY: scrollModal ? 'scroll' : 'hidden' }}
          >
            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="flex-start"
              style={{ marginBottom: 0, flexWrap: 'nowrap', width: 'auto' }}
              // className={classes.tailoredRegionMapTopButtonContainer}
            >
              <Grid
                container
                direction="row"
                justify="flex-start"
                alignItems="center"
                // className={classes.tailoredRegionMapTopButtonContainer}
              >
                <Typography variant="body1">Select Location</Typography>
              </Grid>
              <Grid
                container
                direction="row"
                justify="flex-start"
                alignItems="flex-end"
                className={classes.tailoredRegionMapTopButtonContainer}
              >
                <IconButton>
                  <CloseIcon
                    style={{ color: COLOR_RED, fontSize: 40, marginRight: 0 }}
                    onClick={closeTailoredRegion}
                  />
                </IconButton>
              </Grid>
            </Grid>

            {!isLoading && (!apiCallStatus || (apiCallStatus && apiCallStatus !== 'success')) && (
              <Collapse in={tailorRegionStartInfo}>
                <Alert
                  severity="info"
                  className={classes.tailoredRegionMapAlert}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setTailorRegionStartInfo(false);
                      }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  Zoom in and move around the map to find out your neighbourhood. Click the polygon
                  button on top left of the map to define your own region.{' '}
                  <Link href="https://airbtics.com/how-to-create-a-custom-market/" target="_blank">
                    Here's how it works
                  </Link>
                </Alert>
              </Collapse>
            )}

            {isLoading && (
              <Alert severity="info" style={{ marginBottom: 10 }}>
                We are crunching the numbers now... It takes upto 1 minute!
                <CircularProgress size={20} />
              </Alert>
            )}

            <Paper className={classes.tailoredRegionMapContainer}>
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: 'AIzaSyDUnmEGqix9gziZQjtCQFJ7mCTSDV9yPDk',
                  libraries: ['drawing', 'places', 'visualization'],
                  id: 'onboarding-modal-map',
                }}
                center={
                  locationAllowed
                    ? { lat: homeLat, lng: homeLng }
                    : params.has('lat') &&
                      params.has('lng') &&
                      !isNaN(Number(params.get('lat'))) &&
                      !isNaN(Number(params.get('lng')))
                    ? { lat: Number(params.get('lat')), lng: Number(params.get('lng')) }
                    : { lat: 39.210878338011376, lng: -101.51684523289367 }
                }
                options={mapOptions}
                zoom={locationAllowed ? 12 : 2}
                resetBoundsOnResize
                onChange={handleMapChange}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => {
                  tailoredRegionApiIsLoaded(map, maps);
                  callbackAfterMapIsLoaded(map, maps);
                }}
                onDragEnd={map => handleTailoredRegionMapChange(map)}
              >
                {locationPin}
                {markers}
                <IconButton style={{ position: 'absolute', top: '-197px', left: '-240px', padding:'2px', backgroundColor:'#ffff' }}>
                  <RestartAltIcon
                    style={{
                      color: COLOR_PRIMARY,
                      fontSize: 25,
                    }}
                    onClick={resetTailoredRegion}
                  />
                </IconButton>
              </GoogleMapReact>
            </Paper>

            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              style={{ marginTop: 10 }}
            >
              <Grid
                item
                xs={12}
                // sm={isModalFullScreen ? 12 : 6}
                md={6}
                style={{
                  paddingBottom: '10px',
                }}
              >
                {!isLoading && (apiCallStatus != 'success') ? (
                  <Grid
                    container
                    direction="column"
                    justify="flex-start"
                    // alignItems={isModalFullScreen ? 'center' : 'flex-start'}
                    alignItems="flex-start"
                  >
                    <Typography>{tailoredListings} listings are selected</Typography>

                    <Collapse in={triggerRectangle && apiCallStatus === 'notAllowed'}>
                      {tailoredListings < 10 ? (
                        <Alert severity="error">
                          Please draw a larger square so that it includes more than 10 listings!
                        </Alert>
                      ) : (
                        <Alert severity="error">
                          Please draw a smaller square so that it includes less than 30000 listings!
                        </Alert>
                      )}
                    </Collapse>

                    <Collapse in={apiCallStatus === 'failed'}>
                      <Alert severity="error">
                        Oops! Something went wrong. Please retry. If error persists, contact
                        data@airbtics.com, please.
                      </Alert>
                    </Collapse>
                  </Grid>
                ) : (
                  <Grid>
                    <Typography style={{ marginBottom: 10 }}>
                      Selected region has {tailoredListings} listings.
                    </Typography>
                    {tailorRegionName && (
                      <TextField
                        id="tailoredRegionName"
                        label="Region Name"
                        variant="standard"
                        size="small"
                        defaultValue={tailorRegionName}
                        value={tailorRegionName}
                        type="text"
                        onFocus={() => {
                          setEditTailoredRegionName(true);
                          // loose focus
                          document.getElementById('tailoredRegionName')!.blur();
                        }}
                      />
                    )}
                  </Grid>
                )}
              </Grid>

              <Button
                size="small"
                variant="outlined"
                className={classes.button}
                onClick={setCustomMarket}
                disabled={disableButton}
              >
                Set your own market
              </Button>
            </Grid>
          </div>
        </Grid>
      </Modal>

      {polygonAssistant.isOpen && (
        <PolygonButtonAssistent
          top={polygonAssistant.top + 30}
          left={polygonAssistant.left + 30}
          setPolygonAssistant={setPolygonAssistant}
        />
      )}

      {editTailoredRegionName && (
        <EditTailoredRegionNameModal
          editTailoredRegionName={editTailoredRegionName}
          setEditTailoredRegionName={setEditTailoredRegionName}
          tailorRegionName={tailorRegionName}
          setTailorRegionName={setTailorRegionName}
        />
      )}

      {wrongLatLng && (
        <ErrorDialog
          open={wrongLatLng}
          setOpen={setWrongLatLng}
          title={'Error! Wrong Location Information!'}
          body={`Unfortunately we are unable to find this location. Please draw your location on the map here.`}
        />
      )}
    </>
  );
};

const PolygonButtonAssistent = ({
  top,
  left,
  setPolygonAssistant,
}: {
  top: number;
  left: number;
  setPolygonAssistant: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      top: number;
      left: number;
    }>
  >;
}) => {
  return (
    <div
      style={{
        padding: '10px 10px',
        position: 'fixed',
        zIndex: 5000,
        top: top + 'px',
        left: left + 'px',
        background: '#EBFCFF',
        filter: 'drop-shadow(0 0 5px #ccc)',
        fontFamily: 'Roboto',
      }}
    >
      <div style={{ position: 'relative' }}>
        <svg
          width="28"
          height="14"
          viewBox="0 0 28 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            position: 'absolute',
            transform: 'rotate(-90deg)',
            top: 2,
            left: -30,
          }}
        >
          <path
            d="M28 14C28 14 -2.25221 13.9999 0.1341 14C2.52041 14.0001 4.90673 14.0001 8.4862 11.0001C10.8608 9.00986 16.9112 3.93895 20.7153 0.750697C22.0169 -0.340207 23.9972 0.587473 23.9972 2.28579V6C23.9972 6 23.9972 9.49999 23.9972 12C23.9972 14.0001 28 14 28 14Z"
            fill="#EBFCFF"
          />
        </svg>

        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          Click here to draw your area
          <span
            style={{ marginLeft: '20px', cursor: 'pointer' }}
            onClick={() => {
              setPolygonAssistant(prev => ({ ...prev, isOpen: false }));
            }}
          >
            <svg
              width="14"
              height="16"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.292893 13.2929C-0.0976311 13.6834 -0.0976311 14.3166 0.292893 14.7071C0.683417 15.0976 1.31658 15.0976 1.70711 14.7071L0.292893 13.2929ZM14.7071 1.70711C15.0976 1.31658 15.0976 0.683418 14.7071 0.292894C14.3166 -0.0976305 13.6834 -0.0976305 13.2929 0.292894L14.7071 1.70711ZM1.70711 0.292893C1.31658 -0.0976311 0.683418 -0.0976311 0.292893 0.292893C-0.0976311 0.683417 -0.0976311 1.31658 0.292893 1.70711L1.70711 0.292893ZM13.2929 14.7071C13.6834 15.0976 14.3166 15.0976 14.7071 14.7071C15.0976 14.3166 15.0976 13.6834 14.7071 13.2929L13.2929 14.7071ZM1.70711 14.7071L14.7071 1.70711L13.2929 0.292894L0.292893 13.2929L1.70711 14.7071ZM0.292893 1.70711L13.2929 14.7071L14.7071 13.2929L1.70711 0.292893L0.292893 1.70711Z"
                fill="#CBD5E1"
              />
            </svg>
          </span>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
